import { apiUrl } from '@ai-studio/shared';
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type PointerEvent as ReactPointerEvent, type ReactElement } from 'react';

import { docIO } from '../../lib/doc-io.js';
import { useKeyHint } from '../../lib/shortcuts/index.js';
import { useDocHistory } from '../../lib/use-doc-history.js';
import { useProjectFiles } from '../../state/project-files.js';
import { useWorkspace } from '../../state/workspace.js';
import { ColorField, ContextMenu, IconButton, isTyping, JsonField, NumberField, PropRow, SelectField, TextField, Toggle, VecField, type MenuItem } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import {
  ALIGNMENTS,
  ANCHOR_PRESETS,
  BUILTIN_SHEET,
  ELEMENT_TYPES,
  SELECTABLE,
  alignFactors,
  cssColor,
  flatten,
  newElement,
  nodeAt,
  paddingOf,
  parseUiDoc,
  pathKey,
  rectDefaults,
  resolveStyle,
  serializeUiDoc,
  sheetClasses,
  siblingsOf,
  uniqueId,
  parseWeight,
  richSpans,
  shapeSvgPath,
  sidesOf,
  sidesValue,
  UI_EVENTS,
  UI_STYLE_EXT,
  type Box,
  type ElementType,
  type LayoutGroup,
  type NodePath,
  type StyleSheet,
  type UiDoc,
  type UiNode,
  type Vec2,
} from './ui-doc.js';
import { cssFontFamily, engineLayout, sheetFonts, useCssFonts, useUiLayoutEngine } from './engine-layout.js';
import './ui-editor.css';

const RESOLUTIONS: { label: string; size: Vec2 }[] = [
  { label: '1920 × 1080', size: [1920, 1080] },
  { label: '1280 × 720', size: [1280, 720] },
  { label: '2560 × 1440', size: [2560, 1440] },
  { label: '800 × 600', size: [800, 600] },
  { label: '1080 × 1920 (portrait)', size: [1080, 1920] },
];

const HAS_TEXT = new Set(['text', 'button', 'toggle', 'inputField']);

type Affine = [number, number, number, number, number, number];

const affMul = (m: Affine, o: Affine): Affine => [
  m[0] * o[0] + m[2] * o[1],
  m[1] * o[0] + m[3] * o[1],
  m[0] * o[2] + m[2] * o[3],
  m[1] * o[2] + m[3] * o[3],
  m[0] * o[4] + m[2] * o[5] + m[4],
  m[1] * o[4] + m[3] * o[5] + m[5],
];

/**
 * CSS `transform` (with `transform-origin: 0 0`) for an element whose
 * engine transform is `x` (canvas units, y up), drawn at CSS `origin` in a
 * canvas `height` units tall at `k` CSS px per unit.
 */
function cssTransform(x: Affine | undefined, k: number, height: number, origin: [number, number]): string | undefined {
  if (!x) return undefined;
  const s: Affine = [k, 0, 0, -k, 0, height * k];
  const si: Affine = [1 / k, 0, 0, -1 / k, 0, height];
  const m = affMul(affMul(s, x), si);
  const e = m[0] * origin[0] + m[2] * origin[1] + m[4] - origin[0];
  const f = m[1] * origin[0] + m[3] * origin[1] + m[5] - origin[1];
  return `matrix(${m[0]}, ${m[1]}, ${m[2]}, ${m[3]}, ${e}, ${f})`;
}

/** CSS mask of a radial fill (like the engine's pie: clockwise on screen from `fillOrigin`). */
function radialMask(n: UiNode): string | undefined {
  const method = n.fillMethod ?? 'horizontal';
  if (n.imageType !== 'filled' || !method.startsWith('radial')) return undefined;
  const t = Math.min(1, Math.max(0, n.fillAmount ?? 1));
  const cw = n.fillClockwise !== false;
  // Center (% of the box, y down) and start angle (degrees, 0 = right, clockwise) for clockwise / counter-clockwise.
  let c: [number, number] = [50, 50];
  let start = 90;
  let span = 360;
  if (method === 'radial90') {
    span = 90;
    const table: Record<string, [[number, number], number, number]> = {
      topLeft: [[0, 0], 0, 90],
      topRight: [[100, 0], 90, 180],
      bottomRight: [[100, 100], 180, -90],
      bottomLeft: [[0, 100], -90, 0],
    };
    const [cc, a, b] = table[n.fillOrigin ?? 'bottomLeft'] ?? table.bottomLeft!;
    c = cc;
    start = cw ? a : b;
  } else if (method === 'radial180') {
    span = 180;
    const table: Record<string, [[number, number], number, number]> = {
      top: [[50, 0], 0, 180],
      left: [[0, 50], -90, 90],
      right: [[100, 50], 90, -90],
      bottom: [[50, 100], 180, 0],
    };
    const [cc, a, b] = table[n.fillOrigin ?? 'bottom'] ?? table.bottom!;
    c = cc;
    start = cw ? a : b;
  } else {
    start = ({ right: 0, top: -90, left: 180, bottom: 90 } as Record<string, number>)[n.fillOrigin ?? 'bottom'] ?? 90;
  }
  const sweep = span * t;
  const from = (cw ? start : start - sweep) + 90;
  return `conic-gradient(from ${from}deg at ${c[0]}% ${c[1]}%, #000 0deg ${sweep}deg, transparent ${sweep}deg 360deg)`;
}

interface UiProject {
  /** Built-in preview sheet first, then `settings.ui.styles`, then the document's. */
  sheets: StyleSheet[];
  /** `settings.ui` of the project (default scaler, default font, DPI). */
  settingsUi: { defaultFont?: string; [key: string]: unknown };
}

/** Loads the style sheets the document uses (project `settings.ui.styles` + its own) and `settings.ui`. */
function useUiProject(projectId: number | null, docStyles: string[]): UiProject {
  const [state, setState] = useState<UiProject>({ sheets: [BUILTIN_SHEET], settingsUi: {} });
  const key = docStyles.join('|');
  useEffect(() => {
    if (!projectId) return;
    let cancelled = false;
    void (async () => {
      const io = docIO();
      const paths: string[] = [];
      let settingsUi: UiProject['settingsUi'] = {};
      try {
        const project = JSON.parse(await io.read(projectId, 'prism.project.json')) as { settings?: { ui?: UiProject['settingsUi'] & { styles?: string[] } } };
        settingsUi = project.settings?.ui ?? {};
        paths.push(...(project.settings?.ui?.styles ?? []));
      } catch {
        /* no project styles */
      }
      paths.push(...docStyles);
      const loaded: StyleSheet[] = [BUILTIN_SHEET];
      for (const p of paths) {
        try {
          loaded.push(JSON.parse(await io.read(projectId, p)) as StyleSheet);
        } catch {
          /* missing or broken sheet: the validator reports it */
        }
      }
      if (!cancelled) setState({ sheets: loaded, settingsUi });
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, key]);
  return state;
}

/** Visual editor for one `*.ui.json` (edits the open buffer, autosaves). */
export function UiDocEditor({ path, onShowText }: { path: string; onShowText: () => void }) {
  const file = useWorkspace((s) => s.files[path]);
  const projectId = useWorkspace((s) => s.projectId);
  const setContent = useWorkspace((s) => s.setContent);
  const saveFile = useWorkspace((s) => s.saveFile);
  const entries = useProjectFiles((s) => s.entries);
  const content = file?.content ?? '';
  const parsed = useMemo(() => parseUiDoc(content), [content]);
  const doc = parsed.doc;

  const [selected, setSelected] = useState<NodePath | null>(null);
  const [resolution, setResolution] = useState(0);
  const [menu, setMenu] = useState<{ x: number; y: number; items: MenuItem[] } | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const { sheets, settingsUi } = useUiProject(projectId, doc?.styles ?? []);

  const commit = useCallback(
    (text: string) => {
      setContent(path, text);
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => void saveFile(path), 500);
    },
    [path, setContent, saveFile],
  );

  const history = useDocHistory<string>({
    id: `ui:${path}`,
    label: `UI · ${path.split('/').pop()}`,
    view: 'code',
    read: () => useWorkspace.getState().files[path]?.content ?? '',
    apply: commit,
  });
  const undoKey = useKeyHint('edit.undo');
  const redoKey = useKeyHint('edit.redo');

  const edit = useCallback(
    (fn: (d: UiDoc) => void, label = 'Edit UI', key?: string) => {
      if (!doc) return;
      const next = structuredClone(doc);
      fn(next);
      const text = serializeUiDoc(next);
      if (text === content) return;
      history.record(content, label, key);
      commit(text);
    },
    [doc, content, commit, history],
  );

  const editNode = useCallback(
    (p: NodePath, fn: (n: UiNode) => void, label = 'Edit element', key?: string) =>
      edit(
        (d) => {
          const n = nodeAt(d, p);
          if (n) fn(n);
        },
        label,
        key,
      ),
    [edit],
  );

  // Undo / redo go through the shared undo service (Ctrl+Z, Edit menu, History panel).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (isTyping(e.target)) return;
      if ((e.key === 'Delete' || e.key === 'Backspace') && selected) {
        e.preventDefault();
        removeSelected();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  useEffect(
    () => () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current);
        void saveFile(path);
      }
    },
    [path, saveFile],
  );

  const images = useMemo(
    () => entries.filter((e) => e.kind === 'file' && /\.(png|jpe?g|pixel\.json)$/i.test(e.name)).map((e) => e.path),
    [entries],
  );
  // A newly selected element shows its properties from the top (Id first).
  const propsRef = useRef<HTMLElement | null>(null);
  const selKey = selected ? pathKey(selected) : '';
  useEffect(() => {
    if (propsRef.current) propsRef.current.scrollTop = 0;
  }, [selKey]);
  const styleFiles = useMemo(() => entries.filter((e) => e.kind === 'file' && e.name.endsWith(UI_STYLE_EXT)).map((e) => e.path), [entries]);

  if (!file) return null;
  if (!doc) {
    return (
      <div className="uied uied-broken">
        <Icon name="warning" size={22} />
        <p>This UI document is not valid JSON: {parsed.error}</p>
        <button type="button" className="uied-chip" onClick={onShowText}>
          Fix it as text
        </button>
      </div>
    );
  }

  const node = selected ? nodeAt(doc, selected) : null;
  const sel = node ? selected : null;

  function addElement(type: ElementType) {
    let at: NodePath = [];
    edit((d) => {
      const el = newElement(d, type);
      const parent = sel ? nodeAt(d, sel) : null;
      if (parent) {
        parent.children = [...(parent.children ?? []), el];
        at = [...sel!, parent.children.length - 1];
      } else {
        d.elements.push(el);
        at = [d.elements.length - 1];
      }
    }, `Add ${type}`);
    setSelected(at);
  }

  function removeSelected() {
    if (!sel) return;
    edit((d) => {
      const s = siblingsOf(d, sel);
      s?.list.splice(s.index, 1);
    }, 'Delete element');
    setSelected(null);
  }

  function duplicateSelected() {
    if (!sel || !node) return;
    edit((d) => {
      const s = siblingsOf(d, sel);
      if (!s) return;
      const copy = structuredClone(s.list[s.index]!);
      const rename = (n: UiNode) => {
        if (n.id) n.id = uniqueId(d, n.id);
        n.children?.forEach(rename);
      };
      rename(copy);
      s.list.splice(s.index + 1, 0, copy);
    }, 'Duplicate element');
    setSelected([...sel.slice(0, -1), sel[sel.length - 1]! + 1]);
  }

  function moveSelected(delta: number) {
    if (!sel) return;
    const i = sel[sel.length - 1]!;
    const s = siblingsOf(doc!, sel);
    if (!s || i + delta < 0 || i + delta >= s.list.length) return;
    edit((d) => {
      const t = siblingsOf(d, sel);
      if (!t) return;
      const [n] = t.list.splice(i, 1);
      t.list.splice(i + delta, 0, n!);
    }, delta < 0 ? 'Move element up' : 'Move element down');
    setSelected([...sel.slice(0, -1), i + delta]);
  }

  function addMenu(x: number, y: number) {
    setMenu({
      x,
      y,
      items: ELEMENT_TYPES.map((t) => ({ id: t, label: sel ? `Add ${t} inside` : `Add ${t}`, run: () => addElement(t) })),
    });
  }

  return (
    <div className="uied">
      <div className="uied-toolbar">
        <button type="button" className="uied-chip" onClick={(e) => addMenu(e.clientX, e.clientY)}>
          + Element
        </button>
        <button type="button" className="uied-chip" disabled={!sel} onClick={duplicateSelected}>
          Duplicate
        </button>
        <button type="button" className="uied-chip" disabled={!sel} onClick={() => moveSelected(-1)}>
          ↑
        </button>
        <button type="button" className="uied-chip" disabled={!sel} onClick={() => moveSelected(1)}>
          ↓
        </button>
        <button type="button" className="uied-chip" disabled={!sel} onClick={removeSelected}>
          Delete
        </button>
        <IconButton icon="undo" title={history.undoLabel ? `Undo ${history.undoLabel}` : 'Undo'} shortcut={undoKey} disabled={!history.canUndo} onClick={history.undo} />
        <IconButton icon="redo" title={history.redoLabel ? `Redo ${history.redoLabel}` : 'Redo'} shortcut={redoKey} disabled={!history.canRedo} onClick={history.redo} />
        <span className="uied-spacer" />
        <SelectField
          value={String(resolution)}
          options={RESOLUTIONS.map((r, i) => ({ value: String(i), label: r.label }))}
          onChange={(v) => setResolution(Number(v))}
        />
        <button type="button" className="uied-chip" onClick={onShowText}>
          Edit as text
        </button>
      </div>
      <div className="uied-body">
        <ElementTree doc={doc} selected={sel} onSelect={setSelected} onContext={(x, y) => addMenu(x, y)} />
        <Preview
          doc={doc}
          sheets={sheets}
          settingsUi={settingsUi}
          projectId={projectId}
          screen={RESOLUTIONS[resolution]!.size}
          selected={sel}
          onSelect={setSelected}
          onRect={(p, rect) => editNode(p, (n) => (n.rect = rect), 'Move / resize element', `rect:${pathKey(p)}`)}
        />
        <aside className="uied-props" ref={propsRef}>
          {node && sel ? (
            <NodeProps
              key={pathKey(sel)}
              node={node}
              classes={sheetClasses(sheets)}
              images={images}
              ids={flatten(doc).map((f) => f.node.id).filter((x): x is string => !!x)}
              onChange={(fn) => editNode(sel, fn, 'Edit element', `props:${pathKey(sel)}`)}
            />
          ) : (
            <DocProps doc={doc} styleFiles={styleFiles} onChange={(fn) => edit(fn, 'Edit document', 'doc')} />
          )}
        </aside>
      </div>
      {menu && <ContextMenu x={menu.x} y={menu.y} items={menu.items} onClose={() => setMenu(null)} />}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Element tree
// ---------------------------------------------------------------------------

function ElementTree({
  doc,
  selected,
  onSelect,
  onContext,
}: {
  doc: UiDoc;
  selected: NodePath | null;
  onSelect: (p: NodePath | null) => void;
  onContext: (x: number, y: number) => void;
}) {
  const sk = selected ? pathKey(selected) : '';
  return (
    <div
      className="uied-tree"
      onClick={(e) => e.target === e.currentTarget && onSelect(null)}
      onContextMenu={(e) => {
        e.preventDefault();
        onContext(e.clientX, e.clientY);
      }}
    >
      <div className="uied-tree-head">Elements</div>
      {flatten(doc).map((f) => (
        <button
          type="button"
          key={pathKey(f.path)}
          className={`uied-tree-row${pathKey(f.path) === sk ? ' is-selected' : ''}${f.node.visible === false || f.node.template ? ' is-dim' : ''}`}
          style={{ paddingLeft: 8 + f.depth * 14 }}
          onClick={() => onSelect(f.path)}
        >
          <span className="uied-tree-type">{f.node.type ?? 'panel'}</span>
          <span className="uied-tree-id">{f.node.id || '(no id)'}</span>
          {f.node.template && <span className="uied-tag">template</span>}
        </button>
      ))}
      {doc.elements.length === 0 && <p className="uied-hint">No elements yet. Use “+ Element” or right-click here.</p>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Canvas preview
// ---------------------------------------------------------------------------

function Preview({
  doc,
  sheets,
  settingsUi,
  projectId,
  screen,
  selected,
  onSelect,
  onRect,
}: {
  doc: UiDoc;
  sheets: StyleSheet[];
  settingsUi: UiProject['settingsUi'];
  projectId: number | null;
  screen: Vec2;
  selected: NodePath | null;
  onSelect: (p: NodePath | null) => void;
  onRect: (p: NodePath, rect: NonNullable<UiNode['rect']>) => void;
}) {
  const host = useRef<HTMLDivElement>(null);
  const [avail, setAvail] = useState<Vec2>([800, 450]);
  const [drag, setDrag] = useState<{ path: NodePath; mode: 'move' | 'size'; x: number; y: number; rect: ReturnType<typeof rectDefaults> } | null>(null);
  const [live, setLive] = useState<{ key: string; rect: NonNullable<UiNode['rect']> } | null>(null);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setAvail([Math.max(100, el.clientWidth - 32), Math.max(100, el.clientHeight - 32)]));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const fit = Math.min(avail[0] / screen[0], avail[1] / screen[1]);

  // Live drag preview: lay out a copy with the dragged rect.
  const shown = useMemo(() => {
    if (!live) return doc;
    const copy = structuredClone(doc);
    const n = nodeAt(copy, live.key.split('.').map(Number));
    if (n) n.rect = live.rect;
    return copy;
  }, [doc, live]);

  // The engine's own layout (prism_wasm UiLayout); the built-in sheet is the engine's.
  const fontPaths = useMemo(() => sheetFonts(sheets), [sheets]);
  const engine = useUiLayoutEngine(projectId, fontPaths, settingsUi.defaultFont);
  // Same fonts for the drawn text (engine layout measures with them).
  useCssFonts(projectId, settingsUi.defaultFont ? [...fontPaths, settingsUi.defaultFont] : fontPaths);
  const layout = useMemo(
    () => (engine.state === 'ready' ? engineLayout(engine.engine, shown, sheets.filter((s) => s !== BUILTIN_SHEET), settingsUi, screen) : null),
    [engine.state, engine.state === 'ready' ? engine.fontsVersion : 0, shown, sheets, settingsUi, screen[0], screen[1]],
  );
  const scale = layout?.scale ?? 1;
  const canvas: Vec2 = layout?.canvas ?? screen;
  const px = doc.canvas?.renderMode === 'worldSpace' ? Math.min(screen[0] / canvas[0], screen[1] / canvas[1]) : scale;
  const k = fit * px;
  const boxes = layout?.boxes ?? new Map<string, Box>();
  const sk = selected ? pathKey(selected) : '';

  const toCss = (b: Box): CSSProperties => ({
    left: b.x * k,
    top: (canvas[1] - b.y - b.h) * k,
    width: b.w * k,
    height: b.h * k,
  });

  function startDrag(e: ReactPointerEvent, path: NodePath, mode: 'move' | 'size') {
    e.stopPropagation();
    e.preventDefault();
    onSelect(path);
    const n = nodeAt(doc, path);
    if (!n) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    setDrag({ path, mode, x: e.clientX, y: e.clientY, rect: rectDefaults(n.rect) });
  }

  function dragged(e: ReactPointerEvent) {
    if (!drag) return null;
    const dx = Math.round((e.clientX - drag.x) / k);
    const dy = Math.round((e.clientY - drag.y) / k);
    if (dx === 0 && dy === 0) return null;
    const r = structuredClone(drag.rect);
    if (drag.mode === 'move') r.anchoredPosition = [r.anchoredPosition[0] + dx, r.anchoredPosition[1] - dy];
    else {
      r.sizeDelta = [r.sizeDelta[0] + dx, r.sizeDelta[1] + dy];
      // Keep the top-left corner in place.
      r.anchoredPosition = [r.anchoredPosition[0] + dx * r.pivot[0], r.anchoredPosition[1] - dy * (1 - r.pivot[1])];
    }
    return r;
  }

  const render = (n: UiNode, path: NodePath): ReactElement | null => {
    const key = pathKey(path);
    const b = boxes.get(key);
    if (!b || n.template) return null;
    const st = resolveStyle(sheets, n);
    const p = paddingOf(st.padding);
    const [fx, fy] = alignFactors(st.textAlign as string | undefined);
    const type = n.type ?? 'panel';
    const textColor = cssColor(st.textColor, '#fff');
    const css = toCss(b);
    const blur = Number(st.backdropBlur ?? 0);
    // Borders (one value or per side) drawn inside the rect like the engine.
    const [bt, br, bb, bl] = sidesOf<unknown>(st.borderWidth, 0).map((w) => Number(w) || 0) as [number, number, number, number];
    const [ct, cr, cb, cl] = sidesOf<unknown>(st.borderColor, undefined).map((c) => cssColor(c)) as [string, string, string, string];
    const borderShadow =
      bt > 0 || br > 0 || bb > 0 || bl > 0
        ? [`inset 0 ${bt * k}px 0 0 ${ct}`, `inset ${-br * k}px 0 0 0 ${cr}`, `inset 0 ${-bb * k}px 0 0 ${cb}`, `inset ${bl * k}px 0 0 0 ${cl}`].join(', ')
        : undefined;
    const style: CSSProperties = {
      ...css,
      background: type === 'toggle' || type === 'image' || type === 'line' || type === 'shape' ? undefined : cssColor(n.color ?? st.backgroundColor),
      borderRadius: Number(st.cornerRadius ?? 0) * k,
      boxShadow: borderShadow,
      opacity: n.visible === false ? 0.25 : Number(st.opacity ?? 1) * (n.opacity ?? 1),
      overflow: type === 'scrollView' || n.mask ? 'hidden' : undefined,
      // Rotation and style transforms from the engine layout.
      transform: cssTransform(b.xform, k, canvas[1], [Number(css.left), Number(css.top)]),
      transformOrigin: b.xform ? '0 0' : undefined,
      // Glass: the preview has no 3D frame behind, but shows the blur.
      backdropFilter: blur > 0 ? `blur(${blur * k * 0.5}px) saturate(${Number(st.backdropSaturation ?? 1)})` : undefined,
      pointerEvents: type === 'line' && key !== sk ? 'none' : undefined,
    };
    const text = type === 'dropdown' ? (n.options?.[Math.max(0, n.value ?? 0)] ?? n.text ?? '') : (n.text ?? '');
    const weight = parseWeight(st.fontWeight) ?? 400;
    const shadow = st.textShadowColor !== undefined
      ? (() => {
          const o = (st.textShadowOffset as number[] | undefined) ?? [1, -1];
          return `${(o[0] ?? 0) * k}px ${-(o[1] ?? 0) * k}px ${Number(st.textShadowBlur ?? 0) * k}px ${cssColor(st.textShadowColor)}`;
        })()
      : undefined;
    const outlineW = Number(st.textOutlineWidth ?? 0);
    const rich = st.richText !== false && type !== 'inputField';
    const spans = (t: string) =>
      rich && t.includes('<')
        ? richSpans(t, Number(st.textSize ?? 16)).map((sp, i) => (
            <span
              key={i}
              style={{
                color: sp.color ? cssColor(sp.color, sp.color) : undefined,
                fontWeight: sp.bold ? Math.min(900, weight + 300) : undefined,
                fontStyle: sp.italic ? 'italic' : undefined,
                fontSize: sp.size ? sp.size * k : undefined,
              }}
            >
              {sp.text}
            </span>
          ))
        : t;
    const textBox = (t: string, color: string, extraLeft = 0) => (
      <span
        className="uied-el-text"
        style={{
          left: (p.left + extraLeft) * k,
          right: p.right * k,
          top: p.top * k,
          bottom: p.bottom * k,
          justifyContent: fx === 0 ? 'flex-start' : fx === 1 ? 'flex-end' : 'center',
          alignItems: fy === 0 ? 'flex-start' : fy === 1 ? 'flex-end' : 'center',
          textAlign: fx === 0 ? 'left' : fx === 1 ? 'right' : 'center',
          fontSize: Number(st.textSize ?? 16) * k,
          fontFamily: cssFontFamily(projectId, st.font, sheets, settingsUi.defaultFont, weight),
          fontWeight: weight,
          fontStyle: st.italic === true ? 'italic' : undefined,
          letterSpacing: st.letterSpacing !== undefined ? Number(st.letterSpacing) * k : undefined,
          textShadow: shadow,
          WebkitTextStroke: outlineW > 0 ? `${outlineW * 2 * k}px ${cssColor(st.textOutlineColor, '#000')}` : undefined,
          paintOrder: outlineW > 0 ? 'stroke fill' : undefined,
          color,
          whiteSpace: st.textWrap === false || type === 'inputField' ? 'pre' : 'pre-wrap',
        }}
      >
        {spans(t)}
      </span>
    );
    let content: ReactElement | null = null;
    if (type === 'image') {
      const tint = cssColor(n.color ?? st.imageColor, '#fff');
      const filled = n.imageType === 'filled';
      const fill = filled ? Math.min(1, Math.max(0, n.fillAmount ?? 1)) : 1;
      const method = n.fillMethod ?? 'horizontal';
      const mask = radialMask(n);
      // Linear fills: width or height from the fill origin.
      const box: CSSProperties = mask
        ? { width: '100%', WebkitMaskImage: mask, maskImage: mask }
        : method === 'vertical' && filled
          ? { width: '100%', height: `${fill * 100}%`, top: n.fillOrigin === 'top' ? 0 : 'auto', bottom: n.fillOrigin === 'top' ? 'auto' : 0 }
          : { width: `${fill * 100}%`, left: n.fillOrigin === 'right' ? 'auto' : 0, right: n.fillOrigin === 'right' ? 0 : 'auto' };
      content =
        n.image && n.image.startsWith('rt:') ? (
          <span className="uied-el-img uied-el-rt" style={box}>
            {n.image}
          </span>
        ) : n.image && projectId && !n.image.endsWith('.pixel.json') ? (
          <img className="uied-el-img" style={box} src={apiUrl(`/projects/${projectId}/raw?path=${encodeURIComponent(n.image)}`)} alt="" draggable={false} />
        ) : (
          <span className="uied-el-img" style={{ background: tint, ...box }} />
        );
    } else if (type === 'line') {
      // Lines are drawn by the canvas-wide SVG layer below.
      content = null;
    } else if (type === 'shape') {
      // Path units from the rect's bottom-left (y up), or top-left with yDown.
      const toLocal = (q: [number, number]): [number, number] => [q[0] * k, (n.yDown ? q[1] : b.h - q[1]) * k];
      const paint = (v: unknown, styleColor: unknown) => (v === true ? cssColor(n.color ?? styleColor, '#fff') : v === undefined || v === false ? undefined : cssColor(v));
      content = (
        <svg className="uied-el-shape" width={b.w * k} height={b.h * k} style={{ position: 'absolute', left: 0, top: 0, overflow: 'visible', pointerEvents: 'none' }}>
          {(n.paths ?? []).map((sp, i) => {
            const fill = paint(sp.fill, st.fillColor);
            const stroke = sp.stroke === undefined ? (fill ? undefined : cssColor(n.color ?? st.lineColor, '#fff')) : paint(sp.stroke, st.lineColor);
            return (
              <path
                key={i}
                d={shapeSvgPath(sp.commands ?? [], toLocal)}
                fill={fill ?? 'none'}
                fillRule={sp.fillRule === 'evenOdd' ? 'evenodd' : 'nonzero'}
                stroke={stroke ?? 'none'}
                strokeWidth={Number(sp.lineWidth ?? st.lineWidth ?? 2) * k}
                strokeLinecap={sp.lineCap ?? (st.lineCap as 'butt' | 'round' | 'square' | undefined) ?? 'butt'}
                strokeLinejoin={sp.lineJoin ?? (st.lineJoin as 'miter' | 'round' | 'bevel' | undefined) ?? 'miter'}
              />
            );
          })}
        </svg>
      );
    } else if (type === 'toggle') {
      const size = b.h * k;
      content = (
        <>
          <span className="uied-el-box" style={{ width: size, height: size, background: cssColor(st.backgroundColor), borderRadius: Number(st.cornerRadius ?? 0) * k }}>
            {n.isOn && <span style={{ background: cssColor(st.fillColor, '#fff') }} />}
          </span>
          {textBox(text, textColor, b.h + 4)}
        </>
      );
    } else if (type === 'slider') {
      const t = Math.min(1, Math.max(0, ((n.value ?? 0) - (n.minValue ?? 0)) / Math.max(1e-6, (n.maxValue ?? 1) - (n.minValue ?? 0))));
      const hs = Number(st.handleSize ?? 16) * k;
      content = (
        <>
          <span className="uied-el-fill" style={{ width: `${t * 100}%`, background: cssColor(st.fillColor, '#fff'), borderRadius: Number(st.cornerRadius ?? 0) * k }} />
          <span className="uied-el-handle" style={{ left: `calc(${t * 100}% - ${hs / 2}px)`, width: hs, height: hs, background: cssColor(st.handleColor, '#fff') }} />
        </>
      );
    } else if (type === 'inputField') {
      content = n.text ? textBox(n.text, textColor) : textBox(n.placeholder ?? '', cssColor(st.placeholderColor, 'rgba(255,255,255,0.5)'));
    } else if (HAS_TEXT.has(type) || type === 'dropdown') {
      content = textBox(text, textColor);
    }
    return (
      <div
        key={key}
        className={`uied-el${key === sk ? ' is-selected' : ''}`}
        style={style}
        onPointerDown={(e) => startDrag(e, path, 'move')}
        onPointerMove={(e) => {
          const r = dragged(e);
          if (r && drag) setLive({ key: pathKey(drag.path), rect: r });
        }}
        onPointerUp={(e) => {
          const r = dragged(e);
          if (r && drag) onRect(drag.path, r);
          setDrag(null);
          setLive(null);
        }}
      >
        {content}
        {key === sk && (
          <span
            className="uied-handle"
            onPointerDown={(e) => startDrag(e, path, 'size')}
            onPointerMove={(e) => {
              const r = dragged(e);
              if (r && drag) setLive({ key: pathKey(drag.path), rect: r });
            }}
            onPointerUp={(e) => {
              const r = dragged(e);
              if (r && drag) onRect(drag.path, r);
              setDrag(null);
              setLive(null);
            }}
          />
        )}
      </div>
    );
  };

  if (!layout) {
    return (
      <div className="uied-preview" ref={host}>
        <p className="uied-hint">
          {engine.state === 'loading'
            ? 'Loading the engine layout…'
            : 'The preview uses the engine’s UI layout. Engine runtime not built (or too old) — run pnpm engine:build.'}
        </p>
      </div>
    );
  }

  // Flat, in draw order (parents first) so children sit above parents.
  const all = flatten(shown);
  // Lines (points in their rect, or from / to element centers), in CSS pixels.
  const idBox = new Map<string, Box>();
  for (const f of all) if (f.node.id) idBox.set(f.node.id, boxes.get(pathKey(f.path))!);
  const toPx = (bx: Box | undefined, at: Vec2): [number, number] | null => {
    if (!bx) return null;
    let p: [number, number] = [bx.x + bx.w * at[0], bx.y + bx.h * at[1]];
    if (bx.xform) {
      const x = bx.xform;
      p = [x[0] * p[0] + x[2] * p[1] + x[4], x[1] * p[0] + x[3] * p[1] + x[5]];
    }
    return [p[0] * k, (canvas[1] - p[1]) * k];
  };
  const lines = all
    .filter((f) => f.node.type === 'line' && f.node.visible !== false && !f.node.template)
    .map((f) => {
      const n = f.node;
      const bx = boxes.get(pathKey(f.path));
      const st = resolveStyle(sheets, n);
      let pts: ([number, number] | null)[] = [];
      if (n.from && n.to) pts = [toPx(idBox.get(n.from), n.fromAnchor ?? [0.5, 0.5]), toPx(idBox.get(n.to), n.toAnchor ?? [0.5, 0.5])];
      else if (bx) pts = (n.points ?? []).map((p) => toPx(bx, [bx.w ? p[0] / bx.w : 0, bx.h ? p[1] / bx.h : 0]));
      const ok = pts.filter((p): p is [number, number] => !!p);
      const dash = st.lineDash as number[] | undefined;
      return ok.length >= 2 ? (
        <polyline
          key={pathKey(f.path)}
          points={ok.map((p) => p.join(',')).join(' ')}
          fill="none"
          stroke={cssColor(n.color ?? st.lineColor, '#fff')}
          strokeWidth={Number(st.lineWidth ?? 2) * k}
          strokeLinecap={Number(st.cornerRadius ?? 0) > 0 ? 'round' : 'butt'}
          strokeDasharray={dash ? `${(dash[0] ?? 0) * k} ${(dash[1] ?? 0) * k}` : undefined}
          opacity={Number(st.opacity ?? 1) * (n.opacity ?? 1)}
        />
      ) : null;
    });
  return (
    <div className="uied-preview" ref={host} onPointerDown={(e) => e.target === e.currentTarget && onSelect(null)}>
      <div className="uied-screen" style={{ width: screen[0] * fit, height: screen[1] * fit }} onPointerDown={(e) => e.target === e.currentTarget && onSelect(null)}>
        <div className="uied-canvas" style={{ width: canvas[0] * k, height: canvas[1] * k }}>
          <svg className="uied-lines" width={canvas[0] * k} height={canvas[1] * k}>
            {lines}
          </svg>
          {all.map((f) => (f.node.visible === false && pathKey(f.path) !== sk ? null : render({ ...f.node, children: [] }, f.path)))}
        </div>
      </div>
      <div className="uied-preview-info">
        canvas {Math.round(canvas[0])} × {Math.round(canvas[1])} units · scale {scale.toFixed(2)}
        {layout.error && <span className="uied-error"> · layout error: {layout.error}</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Properties
// ---------------------------------------------------------------------------

/** A document color (`"#rrggbb(aa)"` or `[r, g, b(, a)]`) as numbers for ColorField. */
function colorArray(c: unknown): number[] | undefined {
  if (Array.isArray(c)) return c as number[];
  if (typeof c === 'string' && /^#[0-9a-f]{6}([0-9a-f]{2})?$/i.test(c)) {
    const ch = (i: number) => Math.round((parseInt(c.slice(i, i + 2), 16) / 255) * 1000) / 1000;
    return c.length === 9 ? [ch(1), ch(3), ch(5), ch(7)] : [ch(1), ch(3), ch(5), 1];
  }
  return undefined;
}

/** Fill origins that fit a fill method (like the engine's validator). */
const FILL_ORIGINS: Record<string, string[]> = {
  horizontal: ['left', 'right'],
  vertical: ['bottom', 'top'],
  radial90: ['bottomLeft', 'topLeft', 'topRight', 'bottomRight'],
  radial180: ['bottom', 'right', 'top', 'left'],
  radial360: ['bottom', 'right', 'top', 'left'],
};

function NodeProps({
  node,
  classes,
  images,
  ids,
  onChange,
}: {
  node: UiNode;
  classes: string[];
  images: string[];
  /** Element ids of the document (line ends, ghosts). */
  ids: string[];
  onChange: (fn: (n: UiNode) => void) => void;
}) {
  const type = node.type ?? 'panel';
  const r = rectDefaults(node.rect);
  const set = <K extends keyof UiNode>(k: K, v: UiNode[K] | undefined) =>
    onChange((n) => {
      if (v === undefined || v === '') delete n[k];
      else n[k] = v;
    });
  const setRect = (k: keyof ReturnType<typeof rectDefaults>, v: Vec2) => onChange((n) => (n.rect = { ...rectDefaults(n.rect), [k]: v }));
  const layout: LayoutGroup | undefined = node.layout;
  const setLayout = (k: keyof LayoutGroup, v: unknown) => onChange((n) => (n.layout = { ...(n.layout ?? {}), [k]: v } as LayoutGroup));
  const classListId = 'uied-classes';
  const imageListId = 'uied-images';
  const idListId = 'uied-ids';
  // Inline style quick fields (glass, text effects): written to `style`.
  const st = (node.style ?? {}) as Record<string, unknown>;
  const setStyle = (k: string, v: unknown) =>
    onChange((n) => {
      const s = { ...((n.style ?? {}) as Record<string, unknown>) };
      if (v === undefined || v === '' || (typeof v === 'number' && Number.isNaN(v))) delete s[k];
      else s[k] = v;
      if (Object.keys(s).length) n.style = s;
      else delete n.style;
    });
  const setSub = <K extends 'drag' | 'drop' | 'worldAnchor'>(k: K, field: string, v: unknown) =>
    onChange((n) => {
      const o = { ...((n[k] ?? {}) as Record<string, unknown>) };
      if (v === undefined || v === '') delete o[field];
      else o[field] = v;
      (n as Record<string, unknown>)[k] = o;
    });
  const hasText = HAS_TEXT.has(type) || type === 'dropdown';
  return (
    <div className="uied-props-body">
      <datalist id={classListId}>
        {classes.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
      <datalist id={imageListId}>
        {images.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
      <datalist id={idListId}>
        {ids.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
      <h4>Element</h4>
      <PropRow label="Id" hint="Unique in the document; scripts and events use it.">
        <TextField mono value={node.id} onChange={(v) => set('id', v)} />
      </PropRow>
      <PropRow label="Type">
        <SelectField value={type} options={ELEMENT_TYPES} onChange={(v) => set('type', v as ElementType)} />
      </PropRow>
      <PropRow label="Classes" hint="Style classes from the style sheets, space separated.">
        <TextField mono list={classListId} value={node.class} onChange={(v) => set('class', v.trim())} />
      </PropRow>
      <PropRow label="Visible">
        <Toggle checked={node.visible !== false} onChange={(v) => set('visible', v ? undefined : false)} />
      </PropRow>
      {SELECTABLE.has(type) && (
        <PropRow label="Interactable">
          <Toggle checked={node.interactable !== false} onChange={(v) => set('interactable', v ? undefined : false)} />
        </PropRow>
      )}
      <PropRow label="Opacity" hint="Multiplies the style's opacity (children too).">
        <NumberField step={0.05} min={0} max={1} value={node.opacity ?? 1} onChange={(v) => set('opacity', v === 1 ? undefined : v)} />
      </PropRow>
      <PropRow label="Raycast target" hint="Takes pointer events (enter / exit / clicks). On by default for controls and elements with drag, drop or handlers.">
        <Toggle checked={node.raycastTarget === true} onChange={(v) => set('raycastTarget', v || undefined)} />
      </PropRow>
      <PropRow label="Tooltip" hint="Shown after the pointer rests over the element (settings.ui.tooltip.delay); rich text. Styled by the tooltip class.">
        <TextField value={node.tooltip} placeholder="(none)" onChange={(v) => set('tooltip', v)} />
      </PropRow>
      {!!node.tooltip && (
        <PropRow label="Tooltip classes" hint="Extra style classes of this tooltip, after the tooltip class.">
          <TextField mono list={classListId} value={node.tooltipClass} onChange={(v) => set('tooltipClass', v.trim())} />
        </PropRow>
      )}
      <PropRow label="Template" hint="Not drawn; scripts clone it with ui_instantiate (list items).">
        <Toggle checked={!!node.template} onChange={(v) => set('template', v || undefined)} />
      </PropRow>
      <PropRow label="Inline style" hint='Style properties for this element only, over its classes, e.g. { "textSize": 40, "hover": { "textColor": "#ff2fb4" } }.'>
        <JsonField value={node.style ?? {}} onChange={(v) => set('style', v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length ? (v as Record<string, unknown>) : undefined)} />
      </PropRow>

      <h4>Rect Transform</h4>
      <div className="uied-anchors">
        {ANCHOR_PRESETS.map((a) => {
          const on = a.min[0] === r.anchorMin[0] && a.min[1] === r.anchorMin[1] && a.max[0] === r.anchorMax[0] && a.max[1] === r.anchorMax[1];
          return (
            <button
              type="button"
              key={a.label}
              className={`uied-chip${on ? ' is-on' : ''}`}
              title={`anchors ${a.min.join(',')} → ${a.max.join(',')}`}
              onClick={() =>
                onChange((n) => {
                  const cur = rectDefaults(n.rect);
                  const stretchX = a.min[0] !== a.max[0];
                  const stretchY = a.min[1] !== a.max[1];
                  n.rect = {
                    ...cur,
                    anchorMin: a.min,
                    anchorMax: a.max,
                    pivot: [stretchX ? 0.5 : a.min[0], stretchY ? 0.5 : a.min[1]],
                    anchoredPosition: [0, 0],
                    sizeDelta: [stretchX ? 0 : Math.max(cur.sizeDelta[0], 10), stretchY ? 0 : Math.max(cur.sizeDelta[1], 10)],
                  };
                })
              }
            >
              {a.label}
            </button>
          );
        })}
      </div>
      <PropRow label="Anchor min">
        <VecField size={2} step={0.05} value={r.anchorMin} onChange={(v) => setRect('anchorMin', v as Vec2)} />
      </PropRow>
      <PropRow label="Anchor max">
        <VecField size={2} step={0.05} value={r.anchorMax} onChange={(v) => setRect('anchorMax', v as Vec2)} />
      </PropRow>
      <PropRow label="Pivot">
        <VecField size={2} step={0.05} value={r.pivot} onChange={(v) => setRect('pivot', v as Vec2)} />
      </PropRow>
      <PropRow label="Position" hint="Pivot offset from the anchors (y up).">
        <VecField size={2} step={1} value={r.anchoredPosition} onChange={(v) => setRect('anchoredPosition', v as Vec2)} />
      </PropRow>
      <PropRow label="Size delta" hint="Size when the anchors meet; added to the anchor box when they are apart.">
        <VecField size={2} step={1} value={r.sizeDelta} onChange={(v) => setRect('sizeDelta', v as Vec2)} />
      </PropRow>
      <PropRow label="Scale">
        <VecField size={2} step={0.05} value={r.scale} onChange={(v) => setRect('scale', v as Vec2)} />
      </PropRow>
      <PropRow label="Rotation" hint="Degrees around the pivot, counter-clockwise; children turn with it.">
        <NumberField step={5} value={node.rect?.rotation ?? 0} onChange={(v) => onChange((n) => {
          const cur = rectDefaults(n.rect);
          n.rect = v ? { ...cur, rotation: v } : { ...cur };
          if (!v) delete n.rect.rotation;
        })} />
      </PropRow>

      <h4>Content</h4>
      {HAS_TEXT.has(type) && (
        <PropRow label="Text">
          <TextField value={node.text} onChange={(v) => set('text', v)} />
        </PropRow>
      )}
      {type === 'inputField' && (
        <>
          <PropRow label="Placeholder">
            <TextField value={node.placeholder} onChange={(v) => set('placeholder', v)} />
          </PropRow>
          <PropRow label="Content type">
            <SelectField
              value={(node.contentType as string) ?? 'standard'}
              options={['standard', 'integerNumber', 'decimalNumber', 'alphanumeric', 'password']}
              onChange={(v) => set('contentType', v === 'standard' ? undefined : v)}
            />
          </PropRow>
          <PropRow label="Max characters">
            <NumberField integer step={1} min={0} value={(node.characterLimit as number) ?? 0} onChange={(v) => set('characterLimit', v || undefined)} />
          </PropRow>
        </>
      )}
      {type === 'image' && (
        <>
          <PropRow label="Image">
            <TextField mono list={imageListId} value={node.image} placeholder="(solid color)" onChange={(v) => set('image', v)} />
          </PropRow>
          <PropRow label="Color" hint="Tint of the image, or the fill of a solid-color image.">
            <ColorField value={colorArray(node.color)} onChange={(v) => set('color', v as UiNode['color'])} />
          </PropRow>
          <PropRow label="Image type">
            <SelectField value={node.imageType ?? 'simple'} options={['simple', 'sliced', 'filled']} onChange={(v) => set('imageType', v === 'simple' ? undefined : (v as UiNode['imageType']))} />
          </PropRow>
          {node.imageType === 'filled' && (
            <>
              <PropRow label="Fill amount">
                <NumberField step={0.05} min={0} max={1} value={node.fillAmount ?? 1} onChange={(v) => set('fillAmount', v)} />
              </PropRow>
              <PropRow label="Fill method" hint="radial90 / 180 / 360: pies (cooldowns, radial menus).">
                <SelectField
                  value={node.fillMethod ?? 'horizontal'}
                  options={['horizontal', 'vertical', 'radial90', 'radial180', 'radial360']}
                  onChange={(v) => onChange((n) => {
                    if (v === 'horizontal') delete n.fillMethod;
                    else n.fillMethod = v as UiNode['fillMethod'];
                    if (n.fillOrigin && !FILL_ORIGINS[v]?.includes(n.fillOrigin)) delete n.fillOrigin;
                  })}
                />
              </PropRow>
              <PropRow label="Fill origin">
                <SelectField
                  value={node.fillOrigin ?? FILL_ORIGINS[node.fillMethod ?? 'horizontal']![0]!}
                  options={FILL_ORIGINS[node.fillMethod ?? 'horizontal']!}
                  onChange={(v) => set('fillOrigin', v === FILL_ORIGINS[node.fillMethod ?? 'horizontal']![0] ? undefined : v)}
                />
              </PropRow>
              {(node.fillMethod ?? '').startsWith('radial') && (
                <PropRow label="Clockwise">
                  <Toggle checked={node.fillClockwise !== false} onChange={(v) => set('fillClockwise', v ? undefined : false)} />
                </PropRow>
              )}
            </>
          )}
          <PropRow label="Preserve aspect">
            <Toggle checked={!!node.preserveAspect} onChange={(v) => set('preserveAspect', v || undefined)} />
          </PropRow>
        </>
      )}
      {type === 'slider' && (
        <>
          <PropRow label="Min / max">
            <VecField size={2} labels={['Min', 'Max']} value={[node.minValue ?? 0, node.maxValue ?? 1]} onChange={(v) => onChange((n) => ((n.minValue = v[0]), (n.maxValue = v[1])))} />
          </PropRow>
          <PropRow label="Value">
            <NumberField step={0.05} value={node.value ?? 0} onChange={(v) => set('value', v)} />
          </PropRow>
          <PropRow label="Whole numbers">
            <Toggle checked={!!node.wholeNumbers} onChange={(v) => set('wholeNumbers', v || undefined)} />
          </PropRow>
          <PropRow label="Direction">
            <SelectField value={(node.direction as string) ?? 'leftToRight'} options={['leftToRight', 'rightToLeft', 'bottomToTop', 'topToBottom']} onChange={(v) => set('direction', v === 'leftToRight' ? undefined : v)} />
          </PropRow>
        </>
      )}
      {type === 'toggle' && (
        <>
          <PropRow label="On">
            <Toggle checked={!!node.isOn} onChange={(v) => set('isOn', v || undefined)} />
          </PropRow>
          <PropRow label="Group" hint="Toggles with the same group act as radio buttons.">
            <TextField mono value={node.group as string | undefined} onChange={(v) => set('group', v)} />
          </PropRow>
        </>
      )}
      {type === 'dropdown' && (
        <>
          <PropRow label="Options" hint="Comma separated.">
            <TextField value={(node.options ?? []).join(', ')} onChange={(v) => set('options', v.split(',').map((s) => s.trim()).filter(Boolean))} />
          </PropRow>
          <PropRow label="Selected">
            <NumberField integer step={1} min={0} value={node.value ?? 0} onChange={(v) => set('value', v)} />
          </PropRow>
        </>
      )}
      {type === 'scrollView' && (
        <>
          <PropRow label="Vertical">
            <Toggle checked={node.vertical !== false} onChange={(v) => set('vertical', v ? undefined : false)} />
          </PropRow>
          <PropRow label="Horizontal">
            <Toggle checked={!!node.horizontal} onChange={(v) => set('horizontal', v || undefined)} />
          </PropRow>
        </>
      )}
      {type === 'line' && (
        <>
          <PropRow label="From / to" hint="Connect two elements (after layout), or leave empty and use points.">
            <div className="uied-row">
              <TextField mono list={idListId} value={node.from} placeholder="from id" onChange={(v) => set('from', v)} />
              <TextField mono list={idListId} value={node.to} placeholder="to id" onChange={(v) => set('to', v)} />
            </div>
          </PropRow>
          {node.from && node.to ? (
            <>
              <PropRow label="From anchor" hint="Point in the from element, 0..1 (0.5, 0.5 = center).">
                <VecField size={2} step={0.1} value={node.fromAnchor ?? [0.5, 0.5]} onChange={(v) => set('fromAnchor', v as Vec2)} />
              </PropRow>
              <PropRow label="To anchor">
                <VecField size={2} step={0.1} value={node.toAnchor ?? [0.5, 0.5]} onChange={(v) => set('toAnchor', v as Vec2)} />
              </PropRow>
            </>
          ) : (
            <PropRow label="Points" hint="[[x, y], ...] canvas units from the rect's bottom-left (y up).">
              <JsonField value={node.points ?? []} onChange={(v) => set('points', Array.isArray(v) && v.length ? (v as Vec2[]) : undefined)} />
            </PropRow>
          )}
          <PropRow label="Color" hint="Overrides the style's lineColor.">
            <ColorField value={colorArray(node.color)} onChange={(v) => set('color', v as UiNode['color'])} />
          </PropRow>
          <PropRow label="Width / dash" hint="Style lineWidth and lineDash [dash, gap] (canvas units).">
            <div className="uied-row">
              <NumberField step={1} min={0} value={Number(st.lineWidth ?? 2)} onChange={(v) => setStyle('lineWidth', v)} />
              <JsonField value={st.lineDash ?? null} onChange={(v) => setStyle('lineDash', Array.isArray(v) ? v : undefined)} />
            </div>
          </PropRow>
        </>
      )}
      {type === 'shape' && (
        <>
          <PropRow label="Paths" hint='[{ "commands": [["moveTo", 0, 0], ["lineTo", 20, 0], ["arc", 10, 10, 10, 0, 180], ["close"]], "fill": true, "stroke": "#000", "lineWidth": 2 }]: moveTo, lineTo, arc (cx, cy, r, startDeg, endDeg), arcTo (x1, y1, x2, y2, r), bezierTo, quadTo, close.'>
            <JsonField value={node.paths ?? []} onChange={(v) => set('paths', Array.isArray(v) && v.length ? (v as UiNode['paths']) : undefined)} />
          </PropRow>
          <PropRow label="Y down" hint="Path coordinates from the rect's top-left with y down (Painter2D / HTML canvas); arcs turn clockwise.">
            <Toggle checked={!!node.yDown} onChange={(v) => set('yDown', v || undefined)} />
          </PropRow>
          <PropRow label="Color" hint="Replaces the style's fillColor / lineColor of paths that use them (fill / stroke: true).">
            <ColorField value={colorArray(node.color)} onChange={(v) => set('color', v as UiNode['color'])} />
          </PropRow>
        </>
      )}
      {(type === 'panel' || type === 'container') && (
        <PropRow label="Mask" hint="Clip children to this rect.">
          <Toggle checked={!!node.mask} onChange={(v) => set('mask', v || undefined)} />
        </PropRow>
      )}

      <h4>Layout Group</h4>
      <PropRow label="Arrange children">
        <SelectField
          value={layout?.type ?? (layout ? 'vertical' : 'none')}
          options={['none', 'vertical', 'horizontal', 'grid']}
          onChange={(v) => onChange((n) => (v === 'none' ? delete n.layout : (n.layout = { ...(n.layout ?? {}), type: v as LayoutGroup['type'] })))}
        />
      </PropRow>
      {layout && (
        <>
          <PropRow label="Padding">
            <NumberField step={1} min={0} value={typeof layout.padding === 'number' ? layout.padding : 0} onChange={(v) => setLayout('padding', v)} />
          </PropRow>
          <PropRow label="Spacing">
            <NumberField step={1} value={typeof layout.spacing === 'number' ? layout.spacing : (layout.spacing?.[0] ?? 0)} onChange={(v) => setLayout('spacing', v)} />
          </PropRow>
          <PropRow label="Child alignment">
            <SelectField value={layout.childAlignment ?? 'upperLeft'} options={ALIGNMENTS} onChange={(v) => setLayout('childAlignment', v)} />
          </PropRow>
          {layout.type === 'grid' ? (
            <>
              <PropRow label="Cell size">
                <VecField size={2} step={1} value={layout.cellSize ?? [100, 100]} onChange={(v) => setLayout('cellSize', v)} />
              </PropRow>
              <PropRow label="Constraint">
                <SelectField value={layout.constraint ?? 'flexible'} options={['flexible', 'fixedColumnCount', 'fixedRowCount']} onChange={(v) => setLayout('constraint', v)} />
              </PropRow>
              {layout.constraint && layout.constraint !== 'flexible' && (
                <PropRow label="Count">
                  <NumberField integer step={1} min={1} value={layout.constraintCount ?? 2} onChange={(v) => setLayout('constraintCount', v)} />
                </PropRow>
              )}
            </>
          ) : (
            <>
              <PropRow label="Control size">
                <div className="uied-row">
                  <Toggle label="W" checked={!!layout.childControlWidth} onChange={(v) => setLayout('childControlWidth', v)} />
                  <Toggle label="H" checked={!!layout.childControlHeight} onChange={(v) => setLayout('childControlHeight', v)} />
                </div>
              </PropRow>
              <PropRow label="Force expand">
                <div className="uied-row">
                  <Toggle label="W" checked={!!layout.childForceExpandWidth} onChange={(v) => setLayout('childForceExpandWidth', v)} />
                  <Toggle label="H" checked={!!layout.childForceExpandHeight} onChange={(v) => setLayout('childForceExpandHeight', v)} />
                </div>
              </PropRow>
            </>
          )}
        </>
      )}

      <h4>Look (inline style)</h4>
      <PropRow label="Border width" hint="Canvas units per side (top, right, bottom, left), drawn inside the rect; 0 = none.">
        <VecField
          size={4}
          step={0.5}
          labels={['T', 'R', 'B', 'L']}
          value={sidesOf<unknown>(st.borderWidth, 0).map((w) => Number(w) || 0)}
          onChange={(v) => setStyle('borderWidth', v.every((w) => w === 0) ? undefined : sidesValue(v))}
        />
      </PropRow>
      <PropRow label="Border color" hint="Per side (top, right, bottom, left); sides meet on the corner diagonals.">
        <div className="uied-row">
          {sidesOf<unknown>(st.borderColor, undefined).map((c, i, all) => (
            <ColorField
              key={i}
              value={colorArray(c)}
              onChange={(v) => {
                const next = all.map((x, j) => (j === i ? v : x ?? v));
                setStyle('borderColor', sidesValue(next));
              }}
            />
          ))}
        </div>
      </PropRow>
      <PropRow label="Margin" hint="Space around the element in its parent's layout group (left, right, top, bottom); negative values overlap the neighbours.">
        <VecField
          size={4}
          step={1}
          labels={['L', 'R', 'T', 'B']}
          value={(() => {
            const m = paddingOf(st.margin);
            return [m.left, m.right, m.top, m.bottom];
          })()}
          onChange={(v) =>
            setStyle('margin', v.every((x) => x === 0) ? undefined : v.every((x) => x === v[0]) ? v[0] : { left: v[0], right: v[1], top: v[2], bottom: v[3] })
          }
        />
      </PropRow>
      <PropRow label="Glass blur" hint="backdropBlur: blur what is behind (canvas units, 0 = off); about 120 = frosted glass. Tint multiplies it.">
        <div className="uied-row">
          <NumberField step={10} min={0} value={Number(st.backdropBlur ?? 0)} onChange={(v) => setStyle('backdropBlur', v || undefined)} />
          <NumberField step={0.05} min={0} value={Number(st.backdropSaturation ?? 1)} onChange={(v) => setStyle('backdropSaturation', v === 1 ? undefined : v)} />
        </div>
      </PropRow>
      {Number(st.backdropBlur ?? 0) > 0 && (
        <PropRow label="Glass tint / grain">
          <div className="uied-row">
            <ColorField value={colorArray(st.backdropTint)} onChange={(v) => setStyle('backdropTint', v)} />
            <NumberField step={0.05} min={0} max={1} value={Number(st.backdropNoise ?? 0)} onChange={(v) => setStyle('backdropNoise', v || undefined)} />
          </div>
        </PropRow>
      )}
      {hasText && (
        <>
          <PropRow label="Weight / italic" hint="fontWeight picks a face of a font family (100..900); heavier than the files = synthesized bold.">
            <div className="uied-row">
              <SelectField
                value={String(parseWeight(st.fontWeight) ?? '')}
                options={[{ value: '', label: 'inherit' }, ...[100, 200, 300, 400, 500, 600, 700, 800, 900].map((w) => ({ value: String(w), label: String(w) }))]}
                onChange={(v) => setStyle('fontWeight', v ? Number(v) : undefined)}
              />
              <Toggle label="I" checked={st.italic === true} onChange={(v) => setStyle('italic', v || undefined)} />
            </div>
          </PropRow>
          <PropRow label="Letter spacing" hint="Canvas units after every character.">
            <NumberField step={0.5} value={Number(st.letterSpacing ?? 0)} onChange={(v) => setStyle('letterSpacing', v || undefined)} />
          </PropRow>
          <PropRow label="Text shadow" hint="Color (alpha 0 = none), offset [x, y] (y up: [1, -1] = right and down), blur.">
            <div className="uied-row">
              <ColorField value={colorArray(st.textShadowColor)} onChange={(v) => setStyle('textShadowColor', v)} />
              <VecField size={2} step={0.5} value={(st.textShadowOffset as Vec2 | undefined) ?? [1, -1]} onChange={(v) => setStyle('textShadowOffset', v)} />
              <NumberField step={1} min={0} value={Number(st.textShadowBlur ?? 0)} onChange={(v) => setStyle('textShadowBlur', v || undefined)} />
            </div>
          </PropRow>
          <PropRow label="Text outline" hint="Color and width (canvas units, 0 = none).">
            <div className="uied-row">
              <ColorField value={colorArray(st.textOutlineColor)} onChange={(v) => setStyle('textOutlineColor', v)} />
              <NumberField step={0.25} min={0} value={Number(st.textOutlineWidth ?? 0)} onChange={(v) => setStyle('textOutlineWidth', v || undefined)} />
            </div>
          </PropRow>
          <PropRow label="Rich text" hint="<b>, <i>, <color=#ff0>, <size=20>, <br> in the text (default on; never in input fields).">
            <Toggle checked={st.richText !== false} onChange={(v) => setStyle('richText', v ? undefined : false)} />
          </PropRow>
        </>
      )}

      <h4>Drag &amp; Drop</h4>
      <PropRow label="Draggable" hint="Scripts get beginDrag / drag / endDrag (and drop on the target).">
        <Toggle checked={!!node.drag} onChange={(v) => set('drag', v ? {} : undefined)} />
      </PropRow>
      {node.drag && (
        <>
          <PropRow label="Drag type" hint="What it carries; drop targets list the types they accept.">
            <TextField mono value={node.drag.type} placeholder="item" onChange={(v) => setSub('drag', 'type', v)} />
          </PropRow>
          <PropRow label="Ghost" hint='"copy" (default), "none", or an element id drawn at the pointer.'>
            <TextField mono list={idListId} value={node.drag.ghost} placeholder="copy" onChange={(v) => setSub('drag', 'ghost', v)} />
          </PropRow>
          <PropRow label="Ghost opacity">
            <NumberField step={0.05} min={0} max={1} value={node.drag.ghostOpacity ?? 0.85} onChange={(v) => setSub('drag', 'ghostOpacity', v === 0.85 ? undefined : v)} />
          </PropRow>
          <PropRow label="Buttons" hint="Mouse buttons that drag: [0] left (default), [0, 2] left and right.">
            <JsonField value={node.drag.buttons ?? [0]} onChange={(v) => setSub('drag', 'buttons', Array.isArray(v) && JSON.stringify(v) !== '[0]' ? v : undefined)} />
          </PropRow>
        </>
      )}
      <PropRow label="Drop target" hint="Takes drops; styles dropAccept / dropReject while a drag hovers it.">
        <Toggle checked={!!node.drop} onChange={(v) => set('drop', v ? {} : undefined)} />
      </PropRow>
      {node.drop && (
        <PropRow label="Accepts" hint="Drag types, comma separated (empty = everything).">
          <TextField
            mono
            value={(node.drop.accepts ?? []).join(', ')}
            onChange={(v) => setSub('drop', 'accepts', v.split(',').map((x) => x.trim()).filter(Boolean).length ? v.split(',').map((x) => x.trim()).filter(Boolean) : undefined)}
          />
        </PropRow>
      )}

      <h4>World Anchor</h4>
      <PropRow label="Follow the world" hint="Screen-space documents: the pivot follows an entity / point in 3D (name tags).">
        <Toggle checked={!!node.worldAnchor} onChange={(v) => set('worldAnchor', v ? { offset: [0, 2, 0] } : undefined)} />
      </PropRow>
      {node.worldAnchor && (
        <>
          <PropRow label="Entity" hint="Scene entity id (scripts: ui_set_anchor).">
            <TextField mono value={node.worldAnchor.entity} onChange={(v) => setSub('worldAnchor', 'entity', v)} />
          </PropRow>
          <PropRow label="Offset" hint="Meters added to the point.">
            <VecField size={3} step={0.1} value={node.worldAnchor.offset ?? [0, 0, 0]} onChange={(v) => setSub('worldAnchor', 'offset', v)} />
          </PropRow>
          <PropRow label="Fade distance" hint="[start, end] meters: opacity 1 → 0.">
            <JsonField value={node.worldAnchor.fadeDistance ?? null} onChange={(v) => setSub('worldAnchor', 'fadeDistance', Array.isArray(v) ? v : undefined)} />
          </PropRow>
          <PropRow label="Scale distance / scale" hint="[near, far] meters and [near, far] scale.">
            <div className="uied-row">
              <JsonField value={node.worldAnchor.scaleDistance ?? null} onChange={(v) => setSub('worldAnchor', 'scaleDistance', Array.isArray(v) ? v : undefined)} />
              <VecField size={2} step={0.05} value={node.worldAnchor.scale ?? [1, 1]} onChange={(v) => setSub('worldAnchor', 'scale', v)} />
            </div>
          </PropRow>
          <PropRow label="Occlude" hint="Hidden while colliders are between the camera and the point.">
            <Toggle checked={!!node.worldAnchor.occlude} onChange={(v) => setSub('worldAnchor', 'occlude', v || undefined)} />
          </PropRow>
        </>
      )}

      <h4>Data &amp; Events</h4>
      <PropRow label="Bind" hint='"key" = ui_set_data(key, v); "state.x" = owner script state; "data.x" = owner data component.'>
        <TextField mono value={node.bind} placeholder="e.g. score" onChange={(v) => set('bind', v)} />
      </PropRow>
      <PropRow label="Format" hint='{} is replaced by the bound value; {:.1} rounds to 1 decimal.'>
        <TextField value={node.format} placeholder="Score: {}" onChange={(v) => set('format', v)} />
      </PropRow>
      {(type === 'button' || type === 'toggle') && (
        <PropRow label="On click" hint="Script function called as name(id, value).">
          <TextField mono value={node.onClick} onChange={(v) => set('onClick', v)} />
        </PropRow>
      )}
      {(type === 'toggle' || type === 'slider' || type === 'inputField' || type === 'dropdown') && (
        <PropRow label="On value changed">
          <TextField mono value={node.onValueChanged} onChange={(v) => set('onValueChanged', v)} />
        </PropRow>
      )}
      {type === 'inputField' && (
        <PropRow label="On submit">
          <TextField mono value={node.onSubmit} onChange={(v) => set('onSubmit', v)} />
        </PropRow>
      )}
      <PropRow label="Handlers" hint={`Event → script function, e.g. { "pointerEnter": "show_tip", "drop": "dropped" }. Events: ${UI_EVENTS.join(', ')}.`}>
        <JsonField value={node.on ?? {}} onChange={(v) => set('on', v && typeof v === 'object' && !Array.isArray(v) && Object.keys(v).length ? (v as Record<string, string>) : undefined)} />
      </PropRow>
      <p className="uied-hint">Every script also gets on_ui_event(id, event, value).</p>
    </div>
  );
}

function DocProps({ doc, styleFiles, onChange }: { doc: UiDoc; styleFiles: string[]; onChange: (fn: (d: UiDoc) => void) => void }) {
  const canvas = doc.canvas ?? {};
  const scaler = canvas.scaler ?? {};
  const setCanvas = (k: string, v: unknown) => onChange((d) => (d.canvas = { ...(d.canvas ?? {}), [k]: v }));
  const setScaler = (k: string, v: unknown) => onChange((d) => (d.canvas = { ...(d.canvas ?? {}), scaler: { ...(d.canvas?.scaler ?? {}), [k]: v } }));
  return (
    <div className="uied-props-body">
      <h4>Document</h4>
      <PropRow label="Name" hint="Scripts can address the document by this name.">
        <TextField value={doc.name} onChange={(v) => onChange((d) => (v ? (d.name = v) : delete d.name))} />
      </PropRow>
      <PropRow label="Style sheets" hint="*.uistyle.json files applied after the project's sheets (comma separated).">
        <TextField
          mono
          list="uied-style-files"
          value={(doc.styles ?? []).join(', ')}
          onChange={(v) => onChange((d) => {
            const list = v.split(',').map((s) => s.trim()).filter(Boolean);
            if (list.length) d.styles = list;
            else delete d.styles;
          })}
        />
        <datalist id="uied-style-files">
          {styleFiles.map((f) => (
            <option key={f} value={f} />
          ))}
        </datalist>
      </PropRow>
      <h4>Canvas</h4>
      <PropRow label="Render mode">
        <SelectField value={canvas.renderMode ?? 'screenSpaceOverlay'} options={['screenSpaceOverlay', 'screenSpaceCamera', 'worldSpace']} onChange={(v) => setCanvas('renderMode', v)} />
      </PropRow>
      <PropRow label="Sort order" hint="Higher draws on top and gets input first.">
        <NumberField integer step={1} value={canvas.sortOrder ?? 0} onChange={(v) => setCanvas('sortOrder', v)} />
      </PropRow>
      {canvas.renderMode === 'worldSpace' ? (
        <>
          <PropRow label="Size">
            <VecField size={2} step={10} value={canvas.size ?? [800, 600]} onChange={(v) => setCanvas('size', v)} />
          </PropRow>
          <PropRow label="Units per meter">
            <NumberField step={10} min={1} value={canvas.pixelsPerUnit ?? 100} onChange={(v) => setCanvas('pixelsPerUnit', v)} />
          </PropRow>
          <PropRow label="Face camera" hint="Billboard: turn the canvas to the camera instead of using the entity's rotation.">
            <Toggle checked={canvas.billboard === true} onChange={(v) => setCanvas('billboard', v ? true : undefined)} />
          </PropRow>
          <PropRow label="Fade distance" hint="[start, end] meters from the camera: opacity 1 → 0, hidden beyond (name plates).">
            <JsonField value={canvas.fadeDistance ?? null} onChange={(v) => setCanvas('fadeDistance', Array.isArray(v) ? v : undefined)} />
          </PropRow>
          <PropRow label="Occlusion" hint="Hidden while colliders are between the camera and the canvas center.">
            <Toggle checked={canvas.occlusion === true} onChange={(v) => setCanvas('occlusion', v ? true : undefined)} />
          </PropRow>
        </>
      ) : (
        <>
          <h4>Canvas Scaler</h4>
          <PropRow label="Own scaler" hint="Off = the project's scaler (Project Settings → UI).">
            <Toggle
              checked={canvas.scaler !== undefined}
              onChange={(v) =>
                onChange((d) => {
                  d.canvas = { ...(d.canvas ?? {}) };
                  if (v) d.canvas.scaler = { mode: 'scaleWithScreenSize', referenceResolution: [1920, 1080], match: 0.5 };
                  else delete d.canvas.scaler;
                })
              }
            />
          </PropRow>
          {canvas.scaler !== undefined && (
            <PropRow label="Mode">
              <SelectField value={scaler.mode ?? 'constantPixelSize'} options={['constantPixelSize', 'scaleWithScreenSize', 'constantPhysicalSize']} onChange={(v) => setScaler('mode', v)} />
            </PropRow>
          )}
          {canvas.scaler !== undefined && (scaler.mode ?? 'constantPixelSize') === 'constantPixelSize' && (
            <PropRow label="Scale factor">
              <NumberField step={0.1} min={0.1} value={scaler.scaleFactor ?? 1} onChange={(v) => setScaler('scaleFactor', v)} />
            </PropRow>
          )}
          {canvas.scaler !== undefined && scaler.mode === 'scaleWithScreenSize' && (
            <>
              <PropRow label="Reference resolution">
                <VecField size={2} step={10} labels={['W', 'H']} value={scaler.referenceResolution ?? [1920, 1080]} onChange={(v) => setScaler('referenceResolution', v)} />
              </PropRow>
              <PropRow label="Screen match">
                <SelectField value={scaler.screenMatchMode ?? 'matchWidthOrHeight'} options={['matchWidthOrHeight', 'expand', 'shrink']} onChange={(v) => setScaler('screenMatchMode', v)} />
              </PropRow>
              {(scaler.screenMatchMode ?? 'matchWidthOrHeight') === 'matchWidthOrHeight' && (
                <PropRow label="Match (width ↔ height)">
                  <NumberField step={0.1} min={0} max={1} value={scaler.match ?? 0} onChange={(v) => setScaler('match', v)} />
                </PropRow>
              )}
            </>
          )}
        </>
      )}
      <p className="uied-hint">Select an element to edit it. Show this document with a canvas component, settings.ui.startDocuments or ui_show(path).</p>
    </div>
  );
}
